import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { styleMap } from "lit/directives/style-map.js";
import { marked } from "marked";

interface ResizeState {
  handle: 'top' | 'left' | 'corner';
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

@customElement("chatbot-widget")
export class ChatbotWidget extends LitElement {
  @property({ type: Number, reflect: true, attribute: 'integration-id' }) integrationId = 0;
  @property({ type: String, reflect: true, attribute: 'api-key' }) apiKeyAttr = "";
  @property({ type: String, reflect: true }) conversationId = "";
  @state() private apiKey = "";
  @state() private apiUrl = "";
  @state() botData: any = null;
  @state() private sessionId: string = "";
  @state() loading = false;
  @state() error = "";
  @state() isOpen = false;
  @state() messages: Array<{
    type: "user" | "bot";
    content: string;
    timestamp: Date;
  }> = [];
  @state() currentMessage = "";
  @state() sendingMessage = false;
  @state() currentConversationId: string | null = null;
  @state() conversationTitle: string | null = null;
  @state() loadingConversationHistory = false;
  @state() private initialized = false;
  @state() private isStreaming = false;
  @state() private streamingContent = "";
  @state() private thinkingMessage = "";
  @state() private _containerWidth = 350;
  @state() private _containerHeight = 500;
  private _streamController: AbortController | null = null;
  private _resizeState: ResizeState | null = null;
  private _rafId: number | null = null;

  private formatMessage(content: string): string {
    try {
      marked.setOptions({
        breaks: true,
        gfm: true,
      });

      const html = marked.parse(content);
      return typeof html === "string" ? html : content;
    } catch (error) {
      console.error("Error formatting markdown:", error);
      return content;
    }
  }

  private getStorageKey(): string {
    // Use apiKey if available, else integrationId (backwards compatibility)
    const keyId = this.apiKeyAttr || this.integrationId?.toString() || '';
    return `chatbot_conversation_${keyId}`;
  }

  private initSession(): void {
    // Use apiKey if available, else integrationId (backwards compatibility)
    const keyId = this.apiKeyAttr || this.integrationId?.toString() || '';
    const storageKey = `chatbot_session_${keyId}`;
    let savedSessionId = localStorage.getItem(storageKey);

    if (!savedSessionId) {
      savedSessionId = crypto.randomUUID();
      localStorage.setItem(storageKey, savedSessionId);
      console.log("ChatbotWidget: Created new session ID:", savedSessionId);
    } else {
      console.log("ChatbotWidget: Loaded session ID from localStorage:", savedSessionId);
    }

    this.sessionId = savedSessionId;
  }

  private saveConversationId(conversationId: string): void {
    if (conversationId && this.integrationId) {
      localStorage.setItem(this.getStorageKey(), conversationId);
      console.log(
        "ChatbotWidget: Saved conversation ID to localStorage:",
        conversationId
      );
    }
  }

  private loadConversationId(): string | null {
    if (!this.integrationId) return null;
    const conversationId = localStorage.getItem(this.getStorageKey());
    if (conversationId) {
      console.log(
        "ChatbotWidget: Loaded conversation ID from localStorage:",
        conversationId
      );
    }
    return conversationId;
  }

  private clearConversationId(): void {
    if (this.integrationId) {
      localStorage.removeItem(this.getStorageKey());
      console.log("ChatbotWidget: Cleared conversation ID from localStorage");
    }
  }

  private scrollToBottom() {
    this.updateComplete.then(() => {
      requestAnimationFrame(() => {
        const messagesContainer = this.shadowRoot?.querySelector(
          ".messages-container"
        ) as HTMLElement;
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      });
    });
  }

  private scrollToLatestElement() {
    this.updateComplete.then(() => {
      // Add a small delay to ensure DOM is fully updated
      setTimeout(() => {
        requestAnimationFrame(() => {
          const messagesContainer = this.shadowRoot?.querySelector(
            ".messages-container"
          ) as HTMLElement;
          if (messagesContainer) {
            const messages = messagesContainer.querySelectorAll(
              ".message, .typing-indicator"
            );
            if (messages.length > 0) {
              const latestElement = messages[
                messages.length - 1
              ] as HTMLElement;
              if (latestElement) {
                const elementTop = latestElement.offsetTop;

                // Scroll to show the element at the top of the visible area
                messagesContainer.scrollTop = elementTop;
              }
            }
          }
        });
      }, 50);
    });
  }

  private updateBotColors() {
    if (this.botData?.chat_color) {
      const botColor = this.botData.chat_color;
      const rgb = this.hexToRgb(botColor);
      const hoverColor = this.adjustColor(botColor, -20);

      this.style.setProperty("--bot-color", botColor);
      this.style.setProperty("--bot-color-hover", hoverColor);
      this.style.setProperty("--bot-color-light", `${botColor}1a`);
      this.style.setProperty("--bot-color-shadow", `${botColor}66`);
      if (rgb) {
        this.style.setProperty(
          "--bot-color-rgb",
          `${rgb.r}, ${rgb.g}, ${rgb.b}`
        );
      }

      console.log("ChatbotWidget: Updated bot colors:", {
        color: botColor,
        hover: hoverColor,
        rgb: rgb,
      });
    } else {
      console.log("ChatbotWidget: No bot color found, using defaults");
      this.style.setProperty("--bot-color", "#4287f5");
      this.style.setProperty("--bot-color-hover", "#3367d6");
      this.style.setProperty("--bot-color-light", "rgba(66, 135, 245, 0.1)");
      this.style.setProperty("--bot-color-shadow", "rgba(66, 135, 245, 0.4)");
      this.style.setProperty("--bot-color-rgb", "66, 135, 245");
    }
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    let cleanHex = hex.replace("#", "");

    if (cleanHex.length === 3) {
      cleanHex = cleanHex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  private adjustColor(hex: string, amount: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;

    const r = Math.max(0, Math.min(255, rgb.r + amount));
    const g = Math.max(0, Math.min(255, rgb.g + amount));
    const b = Math.max(0, Math.min(255, rgb.b + amount));

    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initSession();
    this.initializeConversation();
    document.addEventListener('mousemove', this._onResizeMove);
    document.addEventListener('mouseup', this._onResizeEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._streamController) {
      this._streamController.abort();
      this._streamController = null;
    }
    document.removeEventListener('mousemove', this._onResizeMove);
    document.removeEventListener('mouseup', this._onResizeEnd);
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  private async initializeConversation() {
    // Check for apiKeyAttr or integrationId (T-010 priority logic)
    const keyId = this.apiKeyAttr || this.integrationId;
    if (keyId && !this.initialized) {
      this.initialized = true;
      const savedConversationId = this.loadConversationId();
      if (savedConversationId) {
        console.log(
          "ChatbotWidget: Found saved conversation ID:",
          savedConversationId
        );
        this.currentConversationId = savedConversationId;
        await this.loadConversationHistory();
      } else {
        console.log("ChatbotWidget: No saved conversation ID found");
      }
    }
  }

  static styles = css`
    :host {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
      font-family: Arial, sans-serif;
      --bot-color: #4287f5;
      --bot-color-hover: #3367d6;
      --bot-color-light: rgba(66, 135, 245, 0.1);
      --bot-color-shadow: rgba(66, 135, 245, 0.4);
    }

    .floating-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: var(--bot-color);
      border: none;
      color: white;
      font-size: 24px;
      cursor: pointer;
      box-shadow: 0 4px 12px var(--bot-color-shadow);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      z-index: 10000;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0% {
        box-shadow: 0 4px 12px var(--bot-color-shadow);
      }
      50% {
        box-shadow: 0 4px 20px rgba(var(--bot-color-rgb, 66, 135, 245), 0.6),
          0 0 0 8px rgba(var(--bot-color-rgb, 66, 135, 245), 0.1);
      }
      100% {
        box-shadow: 0 4px 12px var(--bot-color-shadow);
      }
    }

    .floating-button:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(var(--bot-color-rgb, 66, 135, 245), 0.6);
      animation: none;
    }

    .resize-wrapper {
      position: absolute;
      bottom: 80px;
      right: 0;
      z-index: 9999;
    }

    .chat-container {
      width: 100%;
      height: 100%;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      border: 1px solid #e0e0e0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      opacity: 0;
      transform: scale(0.3) translateY(20px);
      transform-origin: bottom right;
      transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                  transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      pointer-events: none;
      z-index: 10001;
    }

    .chat-container.open {
      opacity: 1;
      transform: scale(1) translateY(0);
      pointer-events: all;
    }

    .chat-header {
      padding: 16px;
      font-size: 1em;
      font-weight: normal;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e0e0e0;
      min-height: 50px;
      background-color: white;
      color: #333;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    .bot-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }

    .bot-avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
      border: none;
      background-color: transparent;
    }

    .bot-name {
      font-size: 1.3em;
      font-weight: 400;
      color: #555;
    }

    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .new-conversation-button {
      background: var(--bot-color-light);
      border: none;
      color: var(--bot-color);
      cursor: pointer;
      padding: 8px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;
    }

    .new-conversation-button:hover {
      background: rgba(var(--bot-color-rgb, 66, 135, 245), 0.2);
      color: var(--bot-color-hover);
      transform: scale(1.1);
    }

    .new-conversation-icon {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }

    .close-button {
      background: none;
      border: none;
      color: #666;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s ease;
    }

    .close-button:hover {
      background: rgba(0, 0, 0, 0.1);
    }

    .chat-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .messages-container {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .message {
      max-width: 80%;
      padding: 12px 16px;
      border-radius: 18px;
      word-wrap: break-word;
      line-height: 1.4;
      font-size: 15px;
    }

    .message.user {
      align-self: flex-end;
      background: var(--bot-color);
      color: white;
      border-bottom-right-radius: 4px;
    }

    .message.bot {
      align-self: flex-start;
      background: #f1f3f4;
      color: #333;
      border-bottom-left-radius: 4px;
      text-align: left;
    }

    .message.bot h1,
    .message.bot h2,
    .message.bot h3,
    .message.bot h4,
    .message.bot h5,
    .message.bot h6 {
      margin: 0.5em 0 0.3em 0;
      font-weight: 600;
      line-height: 1.2;
      text-align: left;
    }

    .message.bot h1 {
      font-size: 1.3em;
    }
    .message.bot h2 {
      font-size: 1.2em;
    }
    .message.bot h3 {
      font-size: 1.1em;
    }
    .message.bot h4 {
      font-size: 1.05em;
    }
    .message.bot h5 {
      font-size: 1.02em;
    }
    .message.bot h6 {
      font-size: 1em;
    }

    .message.bot p {
      margin: 0.5em 0;
      line-height: 1.4;
      text-align: left;
    }

    .message.bot ul,
    .message.bot ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
      text-align: left;
    }

    .message.bot li {
      margin: 0.2em 0;
      line-height: 1.4;
      text-align: left;
    }

    .message.bot strong,
    .message.bot b {
      font-weight: 600;
    }

    .message.bot em,
    .message.bot i {
      font-style: italic;
    }

    .message.bot code {
      background: rgba(0, 0, 0, 0.1);
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: "Courier New", monospace;
      font-size: 0.93em;
    }

    .message.bot pre {
      background: rgba(0, 0, 0, 0.1);
      padding: 0.8em;
      border-radius: 6px;
      overflow-x: auto;
      margin: 0.5em 0;
      text-align: left;
    }

    .message.bot pre code {
      background: none;
      padding: 0;
      text-align: left;
    }

    .message.bot blockquote {
      border-left: 3px solid #ccc;
      margin: 0.5em 0;
      padding-left: 1em;
      color: #666;
      text-align: left;
    }

    .message.bot a {
      color: var(--bot-color);
      text-decoration: underline;
    }

    .message.bot a:hover {
      color: var(--bot-color-hover);
    }

    .message.bot table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5em 0;
    }

    .message.bot th,
    .message.bot td {
      border: 1px solid #ddd;
      padding: 0.5em;
      text-align: left;
    }

    .message.bot th {
      background: rgba(0, 0, 0, 0.1);
      font-weight: 600;
    }

    .message-input-container {
      padding: 16px;
      border-top: 1px solid #e0e0e0;
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .message-input {
      flex: 1;
      border: 1px solid #ddd;
      border-radius: 20px;
      padding: 10px 16px;
      font-size: 14px;
      outline: none;
      resize: none;
      max-height: 100px;
      min-height: 20px;
    }

    .message-input:focus {
      border-color: var(--bot-color);
    }

    .send-button {
      background: var(--bot-color);
      color: white;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;
    }

    .send-button:hover:not(:disabled) {
      background: var(--bot-color-hover);
    }

    .send-button:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .send-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .welcome-message {
      text-align: center;
      color: #666;
      font-style: italic;
      margin: 20px 0;
    }

    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 12px 16px;
      background: #f1f3f4;
      border-radius: 18px;
      border-bottom-left-radius: 4px;
      align-self: flex-start;
      max-width: 80%;
    }

    .thinking-message {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 12px 16px;
      background: linear-gradient(90deg,
        rgba(128, 128, 128, 0.1) 0%,
        rgba(128, 128, 128, 0.3) 50%,
        rgba(128, 128, 128, 0.1) 100%);
      background-size: 200% 100%;
      animation: shimmer-slide 1.5s ease-in-out infinite;
      border-radius: 18px;
      border-bottom-left-radius: 4px;
      align-self: flex-start;
      max-width: 80%;
      font-size: 14px;
      color: #666;
    }

    .thinking-message span {
      animation: text-pulse 1.5s ease-in-out infinite;
    }

    @keyframes shimmer-slide {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @keyframes text-pulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }

    .typing-dot {
      width: 8px;
      height: 8px;
      background: #666;
      border-radius: 50%;
      animation: typing 1.4s infinite ease-in-out;
    }

    .typing-dot:nth-child(1) {
      animation-delay: -0.32s;
    }

    .typing-dot:nth-child(2) {
      animation-delay: -0.16s;
    }

    @keyframes typing {
      0%,
      80%,
      100% {
        transform: scale(0.8);
        opacity: 0.5;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }

    .error {
      color: #c00;
    }

    .chat-icon {
      width: 24px;
      height: 24px;
      fill: currentColor;
    }

    .close-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .send-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: transparent;
      z-index: 9998;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    .overlay.active {
      opacity: 1;
      pointer-events: all;
    }

    .welcome-message {
      text-align: center;
      color: #666;
      font-style: italic;
      margin: 20px 0;
    }

    .inat-credit {
      text-align: center;
      font-size: 11px;
      color: #aaa;
      margin: 0 16px 2px 0;
    }
    .inat-credit a {
      color: #aaa;
      text-decoration: underline;
      font-size: 11px;
    }

    .streaming-bubble::after {
      content: "▋";
      animation: blink 1s step-end infinite;
    }

    @keyframes blink {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }

    .resize-handle {
      position: absolute;
      z-index: 10002;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .resize-handle:hover {
      opacity: 1;
    }

    .resize-handle--top {
      top: 0;
      left: 8px;
      right: 8px;
      height: 6px;
      cursor: ns-resize;
      background: linear-gradient(to bottom, rgba(0,0,0,0.12), transparent);
      border-radius: 3px 3px 0 0;
    }

    .resize-handle--left {
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 6px;
      cursor: ew-resize;
      background: linear-gradient(to right, rgba(0,0,0,0.12), transparent);
      border-radius: 3px 0 0 3px;
    }

    .resize-handle--corner {
      top: 0;
      left: 0;
      width: 14px;
      height: 14px;
      cursor: nwse-resize;
      border-top: 2px solid rgba(0,0,0,0.2);
      border-left: 2px solid rgba(0,0,0,0.2);
      border-top-left-radius: 6px;
      opacity: 0.4;
    }

    .resize-handle--corner:hover {
      opacity: 0.8;
    }

    @media (max-width: 480px) {
      :host {
        bottom: 16px;
        right: 16px;
      }

      .floating-button {
        width: 56px;
        height: 56px;
        font-size: 22px;
      }

      .header-actions {
        flex-direction: row;
        gap: 4px;
      }

      .new-conversation-button {
        width: 28px;
        height: 28px;
      }

      .new-conversation-icon {
        width: 16px;
        height: 16px;
      }
    }
  `;

  updated(changed: Map<string, any>) {
    // Trigger fetch when apiKeyAttr or integrationId changes (T-010)
    if (changed.has("apiKeyAttr") || changed.has("integrationId")) {
      this.fetchBotData();
      const keyId = this.apiKeyAttr || this.integrationId;
      if (keyId && !this.initialized) {
        this.initSession();
        this.initializeConversation();
      }
      this._loadSavedSize();
    }
    if (changed.has("conversationId") && this.conversationId) {
      this.loadConversationHistory();
    }
    if (changed.has("botData") && this.botData) {
      this.updateBotColors();
    }
  }

  async fetchBotData() {
    // Priority logic: api-key takes precedence over integration-id (T-010)
    // If apiKeyAttr is provided, use it directly for config fetch
    // If not, fallback to integrationId path (backwards compatibility)
    if (!this.apiKeyAttr && !this.integrationId) {
      console.error("ChatbotWidget: No api-key or integration-id provided");
      this.error = "No api-key or integration-id provided";
      return;
    }

    this.loading = true;
    this.error = "";
    this.botData = null;

    console.log("ChatbotWidget: Fetching bot data with:", {
      apiKeyAttr: this.apiKeyAttr ? `${this.apiKeyAttr.slice(0, 12)}...` : null,
      integrationId: this.integrationId
    });

    // Detectar automáticamente la URL del API basándose en el script
    if (!this.apiUrl) {
      const scriptTag = document.querySelector('script[src*="chatbot-widget"]');
      if (scriptTag) {
        const scriptSrc = scriptTag.getAttribute('src') || '';
        const url = new URL(scriptSrc, window.location.href);
        this.apiUrl = `${url.protocol}//${url.host}`;
        console.log("ChatbotWidget: Auto-detected API URL:", this.apiUrl);
      } else {
        // Fallback: usar el mismo origen que la página
        this.apiUrl = window.location.origin;
        console.log("ChatbotWidget: Using page origin as API URL:", this.apiUrl);
      }
    }

    // Determine config URL based on priority (T-011)
    // api-key -> /widget/by-key/{api_key}/config (new path)
    // integration-id -> /widget/{integration_id}/config (legacy path)
    let configUrl: string;
    if (this.apiKeyAttr) {
      configUrl = `${this.apiUrl}/api/v1/widget/by-key/${this.apiKeyAttr}/config`;
      console.log("ChatbotWidget: Using api-key path (new)");
    } else {
      configUrl = `${this.apiUrl}/api/v1/widget/${this.integrationId}/config`;
      console.log("ChatbotWidget: Using integration-id path (legacy fallback)");
    }
    console.log("ChatbotWidget: API URL:", configUrl);

    try {
      const res = await fetch(configUrl);

      console.log("ChatbotWidget: Response status:", res.status);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("ChatbotWidget: Response data:", data);

      // Guardar apiKey y apiUrl de la respuesta
      this.apiKey = data.api_key || '';

      // If response includes integration_id, use it for storage (backwards compat)
      if (data.integration_id && !this.integrationId) {
        this.integrationId = data.integration_id;
      }

      // Adaptar el formato de respuesta del backend FastAPI
      this.botData = {
        name: data.bot_name,
        avatar_url: data.avatar_url,
        welcome_message: data.welcome_message,
        chat_color: data.theme?.primary_color || data.primary_color || '#4287f5'
      };
      console.log("ChatbotWidget: Bot data set:", this.botData);

      this.updateBotColors();

      if (this.botData?.welcome_message && !this.currentConversationId) {
        this.addBotMessage(this.botData?.welcome_message);
      }
    } catch (e: any) {
      console.error("ChatbotWidget: Error fetching bot data:", e);
      this.error = e.message || "Error de conexion";
    } finally {
      this.loading = false;
      console.log("ChatbotWidget: Loading finished, botData:", this.botData);
    }
  }

  async loadConversationHistory() {
    if (!this.sessionId || !this.integrationId || !this.apiKey) return;

    this.loadingConversationHistory = true;
    try {
      const historyUrl = `${this.apiUrl}/api/v1/widget/${this.integrationId}/history/${this.sessionId}`;
      console.log(
        "ChatbotWidget: Loading conversation history for session:",
        this.sessionId
      );
      const response = await fetch(historyUrl, {
        headers: { 'X-API-Key': this.apiKey },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages && Array.isArray(data.messages)) {
          this.messages = data.messages.map((msg: any) => ({
            type: msg.role === "user" ? "user" : "bot",
            content: msg.content,
            timestamp: new Date(msg.timestamp),
          }));
          console.log(
            "ChatbotWidget: Loaded conversation history, messages count:",
            this.messages.length
          );
          this.scrollToBottom();
        }
      } else {
        console.log(
          "ChatbotWidget: Failed to load conversation history, status:",
          response.status
        );
      }
    } catch (error) {
      console.error(
        "ChatbotWidget: Error loading conversation history:",
        error
      );
    } finally {
      this.loadingConversationHistory = false;
    }
  }

  addBotMessage(content: string) {
    this.messages = [
      ...this.messages,
      {
        type: "bot",
        content,
        timestamp: new Date(),
      },
    ];
    this.scrollToLatestElement();
  }

  addUserMessage(content: string) {
    this.messages = [
      ...this.messages,
      {
        type: "user",
        content,
        timestamp: new Date(),
      },
    ];
    this.scrollToLatestElement();
  }

  async startNewConversation() {
    this.messages = [];
    this.currentConversationId = null;
    this.conversationTitle = null;
    this.thinkingMessage = "";
    this.streamingContent = "";
    this.isStreaming = false;
    this.clearConversationId(); // Clear saved conversation ID

    if (this.botData?.welcome_message) {
      this.addBotMessage(this.botData.welcome_message);
    }
  }

  async closeConversation() {
    if (!this.currentConversationId || !this.integrationId) return;

    try {
      // En FastAPI no hay endpoint de cierre, simplemente limpiamos el estado local
      this.currentConversationId = null;
      this.conversationTitle = null;
      this.clearConversationId();
      console.log("ChatbotWidget: Conversation closed locally");
    } catch (error) {
      console.error("ChatbotWidget: Error closing conversation:", error);
    }
  }

  private _isMobile(): boolean {
    return window.innerWidth <= 480;
  }

  private _clampSize(w: number, h: number): { w: number; h: number } {
    const maxW = Math.min(600, window.innerWidth - 40);
    const maxH = Math.min(700, window.innerHeight - 140);
    return {
      w: Math.max(280, Math.min(w, maxW)),
      h: Math.max(350, Math.min(h, maxH)),
    };
  }

  private _loadSavedSize(): void {
    try {
      const saved = localStorage.getItem(`chatbot_size_${this.integrationId}`);
      if (saved) {
        const { w, h } = JSON.parse(saved);
        const clamped = this._clampSize(w, h);
        this._containerWidth = clamped.w;
        this._containerHeight = clamped.h;
      }
    } catch {
      // ignore
    }
  }

  private _saveSize(): void {
    try {
      localStorage.setItem(
        `chatbot_size_${this.integrationId}`,
        JSON.stringify({ w: this._containerWidth, h: this._containerHeight })
      );
    } catch {
      // ignore
    }
  }

  private _onResizeStart(e: MouseEvent, handle: ResizeState['handle']): void {
    if (this._isMobile()) return;
    e.preventDefault();
    this._resizeState = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: this._containerWidth,
      startHeight: this._containerHeight,
    };
  }

  private _onResizeMove = (e: MouseEvent): void => {
    if (!this._resizeState) return;
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (!this._resizeState) return;
      const { handle, startX, startY, startWidth, startHeight } = this._resizeState;
      let newW = startWidth;
      let newH = startHeight;
      if (handle === 'top' || handle === 'corner') {
        newH = startHeight - (e.clientY - startY);
      }
      if (handle === 'left' || handle === 'corner') {
        newW = startWidth - (e.clientX - startX);
      }
      const clamped = this._clampSize(newW, newH);
      this._containerWidth = clamped.w;
      this._containerHeight = clamped.h;
    });
  };

  private _onResizeEnd = (): void => {
    if (!this._resizeState) return;
    this._resizeState = null;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._saveSize();
  };

  async sendMessage() {
    if (!this.currentMessage.trim() || this.sendingMessage || this.isStreaming) return;

    const message = this.currentMessage.trim();
    this.addUserMessage(message);
    this.currentMessage = "";
    this.sendingMessage = true;
    this.streamingContent = "";
    this.thinkingMessage = "";

    // Scroll to show the typing indicator
    this.scrollToLatestElement();

    // Guard: apiUrl or integrationId missing
    if (!this.apiUrl || !this.integrationId) {
      this.addBotMessage("Error: el widget no está configurado correctamente.");
      this.sendingMessage = false;
      return;
    }

    this._streamController = new AbortController();
    const reader_ref: { reader: ReadableStreamDefaultReader<Uint8Array> | null } = { reader: null };

    try {
      const url = `${this.apiUrl}/api/v1/widget/${this.integrationId}/message/stream`;
      console.log("ChatbotWidget: Sending message (stream) to:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({
          message,
          session_id: this.sessionId,
          conversation_id: this.currentConversationId ?? null,
        }),
        signal: this._streamController.signal,
      });

      console.log("ChatbotWidget: Stream response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      reader_ref.reader = reader;
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") continue;

          let event: any;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue;
          }

          if (event.type === "conversation_id") {
            this.saveConversationId(String(event.conversation_id));
            this.currentConversationId = String(event.conversation_id);
          } else if (event.type === "thinking") {
            this.thinkingMessage = event.message || "";
          } else if (event.type === "chunk") {
            this.thinkingMessage = "";
            if (!this.isStreaming) {
              this.sendingMessage = false;
              this.isStreaming = true;
            }
            this.streamingContent += event.content;
            this.scrollToLatestElement();
          } else if (event.type === "done") {
            this.thinkingMessage = "";
            this.addBotMessage(this.streamingContent);
            this.streamingContent = "";
            this.isStreaming = false;
          } else if (event.type === "error") {
            this.thinkingMessage = "";
            this.addBotMessage(event.message || "Error al procesar el mensaje.");
            this.streamingContent = "";
            this.isStreaming = false;
          }
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("ChatbotWidget: Error en streaming:", error);
      this.addBotMessage("Error de conexión. Intenta de nuevo.");
      this.streamingContent = "";
      this.thinkingMessage = "";
    } finally {
      if (reader_ref.reader) {
        try { reader_ref.reader.releaseLock(); } catch { /* already released */ }
      }
      this.sendingMessage = false;
      this.isStreaming = false;
      this.thinkingMessage = "";
      this._streamController = null;
    }
  }

  handleKeyPress(e: KeyboardEvent) {
    e.stopPropagation();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  closeChat() {
    this.isOpen = false;
  }

  handleOverlayClick(e: Event) {
    const target = e.target as HTMLElement;
    const host = this.shadowRoot?.host;

    if (target === e.currentTarget && !host?.contains(target)) {
      this.closeChat();
    }
  }

  render() {
    console.log("ChatbotWidget: Rendering with botData:", this.botData);
    console.log("ChatbotWidget: Loading:", this.loading);
    console.log("ChatbotWidget: Error:", this.error);
    console.log(
      "ChatbotWidget: Current conversation ID:",
      this.currentConversationId
    );

    return html`
      <div
        class="overlay ${this.isOpen ? "active" : ""}"
        @click=${this.handleOverlayClick}
      ></div>

      ${this.botData
        ? html`
            <button
              class="floating-button"
              @click=${(e: Event) => {
                e.stopPropagation();
                this.toggleChat();
              }}
            >
              <svg class="chat-icon" viewBox="0 0 24 24">
                <path
                  d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"
                />
              </svg>
            </button>
          `
        : ""}

      <div
        class="resize-wrapper"
        style=${styleMap({
          width: `${this._containerWidth}px`,
          height: `${this._containerHeight}px`,
          pointerEvents: this.isOpen ? 'all' : 'none',
        })}
      >
        <div
          class="chat-container ${this.isOpen ? "open" : ""}"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="chat-header">
            <div class="bot-info">
              <img
                src="${this.botData?.avatar_url ||
                "https://via.placeholder.com/40"}"
                alt="Bot Avatar"
                class="bot-avatar"
              />
              <div class="bot-name">${this.botData?.name || "Chat"}</div>
            </div>
            <div class="header-actions">
              ${this.currentConversationId
                ? html`
                    <button
                      class="new-conversation-button"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        this.startNewConversation();
                      }}
                      title="Nueva conversación"
                    >
                      <svg class="new-conversation-icon" viewBox="0 0 24 24">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                    </button>
                  `
                : ""}
              <button
                class="close-button"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.closeChat();
                }}
              >
                <svg class="close-icon" viewBox="0 0 24 24">
                  <path
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="chat-body">
            ${this.loading
              ? html`<div class="welcome-message">Cargando datos del bot...</div>`
              : this.error
              ? html`<div class="error">Error: ${this.error}</div>`
              : !this.botData
              ? html`
                  <div class="welcome-message">
                    <div>Sin datos del bot</div>
                    <div style="margin-top:8px; font-size:0.9em; color:#aaa;">
                      Integration ID: ${this.integrationId || "No proporcionado"}
                    </div>
                    <div style="margin-top:4px; font-size:0.8em; color:#ccc;">
                      URL: ${this.apiUrl}
                    </div>
                  </div>
                `
              : html`
                  <div class="messages-container">
                    ${this.loadingConversationHistory
                      ? html`<div class="welcome-message">
                          Cargando historial de conversación...
                        </div>`
                      : this.messages.length === 0
                      ? html`<div class="welcome-message">
                          ${this.botData.welcome_message ||
                          "¡Hola! ¿En qué puedo ayudarte?"}
                        </div>`
                      : this.messages.map(
                          (msg) => html`
                            <div class="message ${msg.type}">
                              ${msg.type === "bot"
                                ? unsafeHTML(this.formatMessage(msg.content))
                                : msg.content}
                            </div>
                          `
                        )}
                    ${this.sendingMessage || this.thinkingMessage
                      ? html`
                          <div class="thinking-message">
                            <span>${this.thinkingMessage || 'Pensando...'}</span>
                          </div>
                        `
                      : ""}
                    ${this.isStreaming
                      ? html`
                          <div class="message bot streaming-bubble">
                            ${unsafeHTML(this.formatMessage(this.streamingContent))}
                          </div>
                        `
                      : ""}
                  </div>
                  <div class="inat-credit">
                    Creado por
                    <a
                      href="https://inteligencianatural.com.ar/"
                      target="_blank"
                      rel="noopener"
                      style="color:inherit;text-decoration:underline;"
                      >INAT</a
                    >
                  </div>
                  <div
                    class="message-input-container"
                    @click=${(e: Event) => e.stopPropagation()}
                  >
                    <textarea
                      class="message-input"
                      placeholder="Escribe tu mensaje..."
                      .value=${this.currentMessage}
                      @input=${(e: Event) => {
                        e.stopPropagation();
                        this.currentMessage = (
                          e.target as HTMLTextAreaElement
                        ).value;
                      }}
                      @keypress=${this.handleKeyPress}
                      @click=${(e: Event) => e.stopPropagation()}
                      ?disabled=${this.sendingMessage || this.isStreaming}
                    ></textarea>
                    <button
                      class="send-button"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        this.sendMessage();
                      }}
                      ?disabled=${!this.currentMessage.trim() ||
                      this.sendingMessage ||
                      this.isStreaming}
                    >
                      <svg class="send-icon" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  </div>
                `}
          </div>
        </div>
        ${this.isOpen && !this._isMobile() ? html`
          <div
            class="resize-handle resize-handle--top"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'top')}
          ></div>
          <div
            class="resize-handle resize-handle--left"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'left')}
          ></div>
          <div
            class="resize-handle resize-handle--corner"
            @mousedown=${(e: MouseEvent) => this._onResizeStart(e, 'corner')}
          ></div>
        ` : ''}
      </div>
    `;
  }
}
